// prettier-ignore
import get from 'lodash/get';

import {
  CANCEL_WAIT_LOGIN,
  EXPIRE_TOKEN,
  LOGIN_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGOUT,
  PASSWORD_CHANGE_FAILURE,
  PASSWORD_CHANGE_REQUEST,
  PASSWORD_CHANGE_SUCCESS,
  REGISTRATION_FAILURE,
  REGISTRATION_REQUEST,
  REGISTRATION_SUCCESS,
  REMOVE_DOCUMENT_TITLE,
  RESET_ACCESSES,
  SAVE_DOCUMENT_TITLE,
  SET_ARCHIVE_WARNING_SHOWN,
  SET_AUTO_EXPIRED_TOKEN,
  SET_LOCALES,
  SET_LOGIN_METHOD,
  SET_REQUIRE_PASSWORD_CHANGE,
  SET_SIGN_OFF_DIALOG_VISIBILITY,
  SET_SSO_LOGIN_FAILURE,
  SET_TIMEZONES,
  SET_TOKEN_STATUS,
  SHOW_IDP_VIEW,
  UPDATE_LOGON_USER,
  UPDATE_TOKEN,
} from '../actions/AuthActions/constants';

const initialState = {
  actorCode: '',
  actorCodeWithDomain: '',
  actorFirstName: '',
  actorLastName: '',
  documentTitleDuringTokenExpiration: null,
  isExternalUser: false,
  isLogonUserChanged: false,
  isSignOffStep: false,
  isSSOLoginFailure: false,
  isTokenExpired: false,
  isTokenUpdated: false,
  licId: null,
  logged_in: false,
  login_error: null,
  loginMethod: null,
  passwordChangeError: null,
  pending: false,
  registrationError: null,
  requirePasswordChange: false,
  requireRegistration: false,
  sessionExpirationErrorCode: '',
  sessionExpirationErrorMessage: '',
  showSignOffDialog: false,
  showFEDSSOPopup: false,
  token: null,
  userLogonCode: null,
  loggedOnRoleCode: '',
  locales: [],
  timezones: [],
  isArchiveMode: false,
  isArchiveUser: false,
  isArchiveWarningAlreadyShown: false,
  waitForLoginSuccess: true,
  smartInsightApplicationURL: '',
  maxAllowedContentLength: 0,
};

function auth(state = initialState, action) {
  switch (action.type) {
    case PASSWORD_CHANGE_REQUEST:
    case LOGIN_REQUEST:
      return { ...state, pending: true, waitForLoginSuccess: true };
    case LOGIN_SUCCESS:
      return {
        ...state,
        actorCode: action.authData.actorCode,
        actorCodeWithDomain: action.authData.actorCodeWithDomain,
        culture: action.authData.culture,
        actorFirstName: action.authData.actorFirstName,
        actorLastName: action.authData.actorLastName,
        isExternalUser: action.authData.isExternalUser,
        tokenLifeSpan: action.authData.tokenLifeSpan,
        isTokenExpired: false,
        isTokenUpdated: false,
        licId: action.authData.licId,
        logged_in: !action.authData.requireRegistration,
        login_error: null,
        loginMethod: get(action, 'authData.loginMethod', state.loginMethod),
        password: action.password, // TODO: stored temporary for 'silent SignOff'. Remember to clean up in action
        pending: false,
        requireRegistration: action.authData.requireRegistration,
        sessionExpirationErrorCode: action.authData.sessionExpirationErrorCode,
        sessionExpirationErrorMessage: action.authData.sessionExpirationErrorMessage,
        token: action.authData.token,
        userLogonCode: action.authData.userLogonCode,
        username: action.authData.username,
        showFEDSSOPopup: false,
        loggedOnRoleCode: action.authData.loggedOnRoleCode,
        isArchiveMode: action.authData.isArchiveMode,
        isArchiveUser: action.authData.isArchiveUser,
        smartInsightApplicationURL: action.authData.smartInsightApplicationURL,
        maxAllowedContentLength: action.authData.maxAllowedContentLength,
      };
    case LOGIN_FAILURE:
      return { ...state, pending: false, login_error: action.loginError };
    case CANCEL_WAIT_LOGIN:
      return { ...state, waitForLoginSuccess: false };
    case PASSWORD_CHANGE_SUCCESS:
      return {
        ...state,
        logged_in: false,
        pending: false,
        passwordChangeError: null,
        requirePasswordChange: false,
      };
    case PASSWORD_CHANGE_FAILURE:
      return { ...state, pending: false, passwordChangeError: action.passwordChangeError };
    case LOGOUT:
      return {
        pending: false,
        logged_in: false,
      };
    case EXPIRE_TOKEN: {
      return {
        ...state,
        isTokenExpired: true,
      };
    }
    case UPDATE_TOKEN: {
      return {
        ...state,
        token: action.data.Token,
        tokenLifeSpan: action.data.TokenLifeSpan,
      };
    }
    case SET_AUTO_EXPIRED_TOKEN: {
      return {
        ...state,
        token: process.env.REACT_APP_EXPIRED_TOKEN,
      };
    }
    case SAVE_DOCUMENT_TITLE: {
      return {
        ...state,
        documentTitleDuringTokenExpiration: action.title,
      };
    }
    case REMOVE_DOCUMENT_TITLE: {
      return {
        ...state,
        documentTitleDuringTokenExpiration: null,
      };
    }
    case SET_LOGIN_METHOD: {
      return {
        ...state,
        loginMethod: action.loginMethod,
      };
    }
    case SET_SIGN_OFF_DIALOG_VISIBILITY: {
      return {
        ...state,
        isSignOffStep: false,
        showSignOffDialog: action.showSignOffDialog,
        showFEDSSOPopup: action.showFEDSSOPopup,
      };
    }
    case SET_SSO_LOGIN_FAILURE: {
      return {
        ...state,
        isSSOLoginFailure: action.isSSOLoginFailure,
        ...(action.isSSOLoginFailure && {
          loginMethod: null,
        }),
      };
    }
    case SET_TOKEN_STATUS: {
      return {
        ...state,
        isTokenUpdated: action.isTokenUpdated,
        showSignOffDialog: get(action, 'showSignOffDialog', state.showSignOffDialog),
        showFEDSSOPopup: get(action, 'showFEDSSOPopup', state.showFEDSSOPopup),
        isSignOffStep: get(action, 'isSignOffStep', state.isSignOffStep),
      };
    }

    case SET_REQUIRE_PASSWORD_CHANGE: {
      return {
        ...state,
        requirePasswordChange: action.isRequired,
        userLogonCode: action.userLogonCode ?? state.userLogonCode,
      };
    }
    case REGISTRATION_REQUEST:
      return { ...state, pending: true };
    case REGISTRATION_FAILURE:
      return { ...state, pending: false, registrationError: action.error };
    case REGISTRATION_SUCCESS:
      return {
        ...state,
        pending: false,
        requireRegistration: false,
        logged_in: !action.data.requireRegistrationApproval,
      };
    case SET_LOCALES:
      return { ...state, locales: action.data.locales ?? [] };
    case SET_TIMEZONES:
      return { ...state, timezones: action.data.timezones ?? [] };
    case RESET_ACCESSES:
      return { ...state, token: null, password: '', pending: false };
    case SHOW_IDP_VIEW: {
      return {
        ...state,
        isSignOffStep: action.isSignOffStep,
        showSignOffDialog: action.isSignOffStep,
        showFEDSSOPopup: !action.isSignOffStep,
      };
    }
    case UPDATE_LOGON_USER: {
      return {
        ...state,
        isLogonUserChanged: action.isLogonUserChanged,
      };
    }

    case SET_ARCHIVE_WARNING_SHOWN: {
      return {
        ...state,
        isArchiveWarningAlreadyShown: true,
      };
    }
    // This action was added only for testing and demo purposes.
    // It simulates the scenario when the authentication token expires.
    case 'SET_EXPIRED_TOKEN': {
      return {
        ...state,
        token: process.env.REACT_APP_EXPIRED_TOKEN,
      };
    }

    default:
      return state;
  }
}

export default auth;
